import { useRef, useState } from 'react'
import { cn, getGalleryImgSrc } from '#app/utils/misc.tsx'
import { Icon } from './ui/icon.tsx'

interface Image {
	id: string
	altText: string | null
}

export function GallerySlider({
	images,
	roomSeo,
	roomTitle,
}: {
	images: object
	roomSeo?: string
	roomTitle: string
}) {
	const slides = Object.values(images).map(image => getGalleryImgSrc(image.id))
	const slidesAltTexts = Object.values(images).map(image => image.altText)

	const [currentIndex, setCurrentIndex] = useState(0)
	const goToPrevious = () => {
		const isFirstSlide = currentIndex === 0
		const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1
		setCurrentIndex(newIndex)
	}
	const goToNext = () => {
		const isLastSlide = currentIndex === slides.length - 1
		const newIndex = isLastSlide ? 0 : currentIndex + 1
		setCurrentIndex(newIndex)
	}

	return (
		<div className="flex items-center justify-between gap-2 max-md:relative md:justify-center lg:gap-10">
			<div
				className="order-first max-md:absolute max-md:h-full"
				onClick={goToPrevious}
			>
				<Icon
					className="h-full w-12 cursor-pointer max-md:opacity-70 md:h-10 md:w-10 md:hover:opacity-70"
					name="caret-left"
				/>
			</div>
			<div
				className="order-last max-md:absolute max-md:right-0 max-md:h-full"
				onClick={goToNext}
			>
				<Icon
					className="h-full w-12 cursor-pointer max-md:opacity-70 md:h-10 md:w-10 md:hover:opacity-70"
					name="caret-right"
				/>
			</div>

			<div className="h-[25vh] min-h-[250px] w-full md:max-w-4/5 lg:h-[50vh] lg:max-h-[550px] lg:min-h-[550px]">
				{slides.map((slide, index) => (
					<img
						key={index}
						className={cn(
							'pointer-events-none mx-auto rounded-2xl h-full',
							// h-full w-full bg-cover bg-center object-cover
							index === currentIndex ? '' : 'hidden',
						)}
						src={slide}
						alt={slidesAltTexts[index] ?? roomSeo ?? roomTitle}
					/>
				))}
			</div>
		</div>
	)
}

export function RoomPreviewImagesSlider({
	images,
	roomSeo,
	roomTitle,
}: {
	images: object
	roomSeo?: string
	roomTitle: string
}) {
	const slides = Object.values(images).map(image => getGalleryImgSrc(image.id))
	const slidesAltTexts = Object.values(images).map(image => image.altText)

	const [currentIndex, setCurrentIndex] = useState(0)
	const goToPrevious = () => {
		const isFirstSlide = currentIndex === 0
		const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1
		setCurrentIndex(newIndex)
	}
	const goToNext = () => {
		const isLastSlide = currentIndex === slides.length - 1
		const newIndex = isLastSlide ? 0 : currentIndex + 1
		setCurrentIndex(newIndex)
	}

	return (
		<div className="relative flex h-[50vh] justify-center max-md:max-h-[300px] md:max-h-[550px] md:min-h-[350px]">
			<div
				className="absolute left-5 z-1000 order-first max-md:bottom-5 md:top-5"
				onClick={goToPrevious}
			>
				<Icon
					className="cursor-pointer text-background max-md:opacity-70 md:hover:opacity-70"
					name="caret-left"
					size="4xl"
				/>
			</div>
			<div
				className="absolute right-5 z-1000 order-last max-md:bottom-5 md:top-5"
				onClick={goToNext}
			>
				<Icon
					className="cursor-pointer text-background max-md:opacity-70 md:hover:opacity-70"
					name="caret-right"
					size="4xl"
				/>
			</div>

			<div className="z-100 w-full">
				{slides.map((slide, index) => (
					<img
						key={index}
						className={cn(
							'pointer-events-none h-full w-full rounded-2xl bg-cover bg-center object-cover',
							index === currentIndex ? '' : 'hidden',
						)}
						src={slide}
						alt={slidesAltTexts[index] ?? roomSeo ?? roomTitle}
					/>
				))}
			</div>
			<div className=" absolute inset-0 z-100 rounded-2xl bg-gradient-to-l from-transparent to-black opacity-60" />
		</div>
	)
}

export function GalleryImagesSlider({ images }: { images: Image[] }) {
	const slides = images.map(image => getGalleryImgSrc(image.id))
	const slidesAltTexts = images.map(image => image.altText)

	const [currentIndex, setCurrentIndex] = useState(0)
	const sliderRef = useRef<HTMLDivElement>(null)
	const imageWidth = 80

	const goToSlide = (index: number) => {
		setCurrentIndex(index)
	}

	const goToPrevious = () => {
		if (currentIndex > 0) {
			setCurrentIndex(prevIndex => prevIndex - 1)
			sliderRef.current?.scrollBy({ left: -imageWidth, behavior: 'smooth' })
		}
	}

	const goToNext = () => {
		if (currentIndex < slides.length - 1) {
			setCurrentIndex(prevIndex => prevIndex + 1)
			sliderRef.current?.scrollBy({ left: imageWidth, behavior: 'smooth' })
		}
	}

	return (
		<div className="flex flex-col gap-10">
			<div className="relative flex items-center justify-between gap-2 max-md:relative md:justify-center lg:gap-10">
				<div
					className={cn(
						currentIndex > 0 ? "cursor-pointer" : "opacity-40 pointer-events-none",
						'absolute z-3 md:hidden',
					)}
					onClick={goToPrevious}
				>
					<Icon className="h-full w-12" name="caret-left" />
				</div>
				<div
					className={cn(
						currentIndex < slides.length - 1 ? "cursor-pointer" : "opacity-40 pointer-events-none",
						'absolute right-0 z-3 md:hidden',
					)}
					onClick={goToNext}
				>
					<Icon className="h-full w-12" name="caret-right" />
				</div>

				<div className="relative z-2 h-[25vh] min-h-[250px] flex justify-center w-full lg:h-[60vh] lg:max-h-[550px] lg:min-h-[500px]">
					{slides.map((slide, index) => (
						<img
							key={index}
							className={cn(
								// 'pointer-events-none absolute h-full w-full rounded-2xl bg-cover bg-center object-cover',
								'mx-auto rounded-2xl h-full absolute transition-opacity duration-300 pointer-events-none',
								index === currentIndex ? 'opacity-100' : 'opacity-0',
							)}
							src={slide}
							alt={slidesAltTexts[index] ?? ''}
						/>
					))}
				</div>
			</div>

			<div className="flex items-center justify-between gap-2 max-md:relative md:justify-center lg:gap-10">
				<div
					className={cn(
						currentIndex > 0 ? "cursor-pointer" : "opacity-40 pointer-events-none",
						'max-md:hidden',
					)}
					onClick={goToPrevious}
				>
					<Icon
						className="h-full w-12 max-md:opacity-70 md:h-10 md:w-10 md:hover:opacity-70"
						name="caret-left"
					/>
				</div>
				<div
					ref={sliderRef}
					className="no-scrollbar flex max-w-[500px] gap-2 overflow-x-auto"
				>
					{slides.map((slide, index) => (
						<div
							key={index}
							className="gallery-slide-selector scroll-snap-align-start h-20 w-20 flex-none cursor-pointer rounded-lg border-2 border-transparent object-cover"
							onClick={() => goToSlide(index)}
						>
							<img
								src={slide}
								alt={slidesAltTexts[index] ?? ''}
								className={cn(
									'h-full w-full rounded-lg border-2 border-transparent object-cover',
									index === currentIndex ? 'border-white' : '',
								)}
							/>
						</div>
					))}
				</div>

				<div
					className={cn(
						currentIndex < slides.length - 1 ? "cursor-pointer" : "opacity-40 pointer-events-none",
						'max-md:hidden',
					)}
					onClick={goToNext}
				>
					<Icon
						className="h-full w-12 max-md:opacity-70 md:h-10 md:w-10 md:hover:opacity-70"
						name="caret-right"
					/>
				</div>
			</div>
		</div>
	)
}
